import { Box } from '@material-ui/core';
import { PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { OrchestrationEditPanel } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/OrchestrationEditPanel';
import { OrchestrationTable } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/OrchestrationTable';
import { useMerchantLenderOrchestration } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/hooks/useMerchantLenderOrchestration';
import { CircularLoading } from '@/components/shared/components/CircularLoading';
import { CustomAlert } from '@/components/shared/components/CustomAlert';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { ModuleCorePermissionProps, PendingDataType } from '@/config/types';
import { useSetPageLoadingWhenRequesting } from '@/hooks/requests/useSetPageLoadingWhenRequesting';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const MerchantLenderOrchestration = ({ canEdit = false }: ModuleCorePermissionProps) => {
  const { merchantId = '' } = useParams();
  const { rows, lendersByPaymentMethodType, options, loading, loaded, error, lendersError, apply, applying } =
    useMerchantLenderOrchestration(merchantId);
  const [editingType, setEditingType] = useState<PaymentMethodType | null>(null);

  useSetPageLoadingWhenRequesting('LENDER_ORCHESTRATION', [loading]);

  const toggleEdit = (paymentMethodType: PaymentMethodType) =>
    setEditingType((current) => (current === paymentMethodType ? null : paymentMethodType));

  const content = () => {
    if (error) {
      return <PendingDataWarning type={PendingDataType.NO_LENDER_CONFIGURATION_FOUND} />;
    }
    if (!loaded) {
      return <CircularLoading />;
    }
    if (!rows.length) {
      return <PendingDataWarning type={PendingDataType.NO_DATA_TO_DISPLAY} />;
    }

    return (
      <>
        {lendersError && (
          <Box mb={2}>
            <CustomAlert severity="warning" title="LENDER_ORCHESTRATION_LENDERS_LOAD_ERROR" />
          </Box>
        )}
        <OrchestrationTable
          rows={rows}
          lendersByPaymentMethodType={lendersByPaymentMethodType}
          canEdit={canEdit}
          editingType={editingType}
          onToggleEdit={toggleEdit}
          renderEditPanel={(row) => (
            <OrchestrationEditPanel
              row={row}
              options={options}
              lenderNames={lendersByPaymentMethodType[row.paymentMethodType] ?? []}
              applying={applying}
              onApply={async (config) => {
                await apply(row.paymentMethodType, config);
                setEditingType(null);
              }}
            />
          )}
        />
        <Box mt={3}>
          <CustomAlert
            severity="info"
            title="LENDER_ORCHESTRATION_API_INFO_TITLE"
            description="LENDER_ORCHESTRATION_API_INFO_TEXT"
          />
        </Box>
      </>
    );
  };

  return (
    <SubSectionWrapper title="LENDER_ORCHESTRATION_SUBTITLE" titleTooltip="LENDER_ORCHESTRATION_SUBTITLE_TEXT">
      {content()}
    </SubSectionWrapper>
  );
};

export const MerchantLenderOrchestrationModule = withPermissionsModule(MerchantLenderOrchestration);
